import React, { useEffect, useState } from 'react';
import { api } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import SafeIcon from '../../common/SafeIcon';
import { FiAlertCircle, FiUserCheck, FiUserX, FiPlus } from 'react-icons/fi';

const ROLES = ['SUPER_ADMIN', 'OWNER', 'MANAGER', 'CLERK', 'INSPECTOR'];

export default function Users() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterTenant, setFilterTenant] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    name: '',
    tenant_id: '',
    role: 'CLERK'
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [userData, tenantData] = await Promise.all([
        api.getUsers(filterTenant || undefined),
        api.getTenants()
      ]);
      setUsers(userData);
      setTenants(tenantData);
    } catch (err) {
      console.error(err);
      setError('Failed to load users. Ensure your profile has SUPER_ADMIN role.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [filterTenant]);

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setError(null);
    setCreating(true);
    try {
      await api.createUser(
        newUser.email,
        newUser.password,
        newUser.name,
        newUser.tenant_id || null,
        newUser.role
      );
      setIsAdding(false);
      setNewUser({ email: '', password: '', name: '', tenant_id: '', role: 'CLERK' });
      fetchData();
    } catch (err) {
      console.error('Create user error:', err);
      setError(err.message || 'Failed to create user');
    } finally {
      setCreating(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    if (userId === currentUser?.id && newRole !== 'SUPER_ADMIN') {
      if (!confirm('You are changing your own role away from SUPER_ADMIN. Are you sure?')) return;
    }
    try {
      await api.updateUserRole(userId, newRole);
      fetchData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleToggleActive = async (userId, currentlyActive) => {
    if (userId === currentUser?.id) {
      alert('You cannot deactivate your own account.');
      return;
    }
    try {
      await api.updateUserStatus(userId, !currentlyActive);
      fetchData();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white">User Management</h2>
          <p className="text-sm text-slate-500">Manage user roles and access across tenants.</p>
        </div>
        <Button onClick={() => setIsAdding(true)} icon={FiPlus}>Create User</Button>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-4 text-sm text-red-700 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-100 dark:border-red-900/30">
          <SafeIcon icon={FiAlertCircle} className="h-5 w-5 shrink-0" />
          <p>{error}</p>
        </div>
      )}

      {isAdding && (
        <Card className="border-primary-200 dark:border-primary-900 shadow-lg">
          <CardHeader>
            <CardTitle>Create New User</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleCreateUser} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Full Name"
                value={newUser.name}
                onChange={e => setNewUser({ ...newUser, name: e.target.value })}
                required
                placeholder="John Smith"
              />
              <Input
                label="Email Address"
                type="email"
                value={newUser.email}
                onChange={e => setNewUser({ ...newUser, email: e.target.value })}
                required
                placeholder="user@example.com"
              />
              <Input
                label="Initial Password"
                type="password"
                value={newUser.password}
                onChange={e => setNewUser({ ...newUser, password: e.target.value })}
                required
                placeholder="Min 6 characters"
              />
              <div className="w-full">
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Assign to Tenant
                </label>
                <select
                  value={newUser.tenant_id}
                  onChange={e => setNewUser({ ...newUser, tenant_id: e.target.value })}
                  required
                  className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 dark:bg-slate-900 dark:border-slate-700 dark:text-white"
                >
                  <option value="">Select Tenant</option>
                  {tenants.map(t => (
                    <option key={t.id} value={t.id}>{t.company_name}</option>
                  ))}
                </select>
              </div>
              <div className="w-full">
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Role
                </label>
                <select
                  value={newUser.role}
                  onChange={e => setNewUser({ ...newUser, role: e.target.value })}
                  className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 dark:bg-slate-900 dark:border-slate-700 dark:text-white"
                >
                  {ROLES.map(r => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2 flex justify-end gap-2 pt-2">
                <Button type="button" variant="secondary" onClick={() => setIsAdding(false)}>Cancel</Button>
                <Button type="submit" isLoading={creating}>Create User</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <div className="flex items-center gap-3">
        <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Filter by Tenant:</label>
        <select
          value={filterTenant}
          onChange={(e) => setFilterTenant(e.target.value)}
          className="h-10 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 dark:bg-slate-900 dark:border-slate-700 dark:text-white"
        >
          <option value="">All Tenants</option>
          {tenants.map(t => (
            <option key={t.id} value={t.id}>{t.company_name}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-500">Loading users...</div>
      ) : users.length === 0 ? (
        <div className="text-center py-12 bg-white dark:bg-slate-900 rounded-lg border border-dashed border-slate-300 dark:border-slate-800">
          <p className="text-slate-500">No users found.</p>
        </div>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 dark:border-slate-800">
                    <th className="text-left p-4 font-semibold text-slate-600 dark:text-slate-400">Name</th>
                    <th className="text-left p-4 font-semibold text-slate-600 dark:text-slate-400">Email</th>
                    <th className="text-left p-4 font-semibold text-slate-600 dark:text-slate-400">Tenant</th>
                    <th className="text-left p-4 font-semibold text-slate-600 dark:text-slate-400">Role</th>
                    <th className="text-left p-4 font-semibold text-slate-600 dark:text-slate-400">Status</th>
                    <th className="text-right p-4 font-semibold text-slate-600 dark:text-slate-400">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(u => (
                    <tr key={u.id} className="border-b border-slate-100 dark:border-slate-800 last:border-0">
                      <td className="p-4 font-medium text-slate-900 dark:text-white">
                        {u.name || 'Unnamed'}
                        {u.id === currentUser?.id && (
                          <span className="ml-2 text-xs text-slate-400">(you)</span>
                        )}
                      </td>
                      <td className="p-4 text-slate-600 dark:text-slate-400">{u.email}</td>
                      <td className="p-4 text-slate-600 dark:text-slate-400">
                        {u.tenant?.company_name || <span className="italic text-slate-400">None</span>}
                      </td>
                      <td className="p-4">
                        <select
                          value={u.role}
                          onChange={(e) => handleRoleChange(u.id, e.target.value)}
                          className="h-8 rounded border border-slate-300 bg-white px-2 text-xs font-medium dark:bg-slate-900 dark:border-slate-700 dark:text-white"
                        >
                          {ROLES.map(r => (
                            <option key={r} value={r}>{r}</option>
                          ))}
                        </select>
                      </td>
                      <td className="p-4">
                        <Badge variant={u.is_active ? 'success' : 'default'}>
                          {u.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </td>
                      <td className="p-4 text-right">
                        <button
                          onClick={() => handleToggleActive(u.id, u.is_active)}
                          className={`inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium transition-colors ${
                            u.is_active
                              ? 'text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20'
                              : 'text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20'
                          }`}
                          title={u.is_active ? 'Deactivate user' : 'Activate user'}
                        >
                          <SafeIcon icon={u.is_active ? FiUserX : FiUserCheck} className="h-4 w-4" />
                          {u.is_active ? 'Deactivate' : 'Activate'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
